/**
 * ─────────────────────────────────────────────────────────────────────────────
 * @module    public/js/modals/noRespModal.js
 * @function  openNoRespModal
 * @layer     ui-modal
 * @summary   Prompts for voicemail or message outcome when a call is unanswered.
 *
 * @description
 * • Presents radio options for voicemail, message, out of office or none.
 * • Resolves with the selected option, optional next-touch date and whether to call again or proceed.
 *
 * @author    Auto-Generated
 * @version   1.0.0
 * @since     2025-06-23
 * ─────────────────────────────────────────────────────────────────────────────
 */
import { applyFocusModeTooltips } from '../focusmode/tooltipService.js';

const DEFAULT_NO_RESP_REASONS = [
  {
    label: 'Left a voicemail',
    value: 'voicemail',
    tooltip: 'Log that you left a voicemail and move on.',
    allowsNextTouch: false,
    deleteContactInfo: false,
  },
  {
    label: 'Left a message',
    value: 'message',
    tooltip: 'Log that you left a message with a person or channel.',
    allowsNextTouch: false,
    deleteContactInfo: false,
  },
  {
    label: 'Did not leave a message',
    value: 'none',
    tooltip: 'Log no response without leaving a message.',
    allowsNextTouch: false,
    deleteContactInfo: false,
  },
  {
    label: 'Out of Office',
    value: 'outOfOffice',
    tooltip: 'Set the next touch date before saving.',
    allowsNextTouch: true,
    deleteContactInfo: false,
  },
  {
    label: 'Disconnected / number out of service',
    value: 'disconnected',
    tooltip: 'Log that this number is disconnected or out of service.',
    allowsNextTouch: false,
    deleteContactInfo: false,
  },
  {
    label: 'Wrong person / wrong number',
    value: 'wrong',
    tooltip: 'Log bad contact details for cleanup.',
    allowsNextTouch: false,
    deleteContactInfo: false,
  },
];

export async function openNoRespModal(opts = {}) {
  const di = (await import('../bootstrap/app.js')).default;
  const modalService = di.modalService || di.modal;
  const { fsService } = di;
  const hasAnother = !!opts.hasAnotherLine;
  const enableTooltips =
    opts.enableTooltips ??
    document.body?.getAttribute('data-focus-tooltips') !== 'false';

  let noRespReasons = DEFAULT_NO_RESP_REASONS;
  try {
    const config = await fsService.getDocument('AppConfig/outcomes');
    const reasons = (config?.noRespReasons || [])
      .map((reason) => ({
        label: `${reason.label ?? ''}`.trim(),
        value: `${reason.value ?? reason.label ?? ''}`.trim(),
        tooltip: `${reason.tooltip ?? ''}`.trim(),
        allowsNextTouch: !!reason.allowsNextTouch,
        deleteContactInfo: reason.deleteContactInfo === true,
      }))
      .filter((reason) => reason.label.length > 0 && reason.value.length > 0);

    if (!config?.noRespReasons || config.noRespReasons.length === 0) {
      await fsService
        .setDocument(
          'AppConfig/outcomes',
          { noRespReasons: DEFAULT_NO_RESP_REASONS },
          { merge: true }
        )
        .catch((err) =>
          console.warn('[NoRespModal] failed to seed /AppConfig/outcomes', err)
        );
    }

    if (reasons.length > 0) noRespReasons = reasons;
  } catch (err) {
    console.warn('[NoRespModal] failed to load /AppConfig/outcomes', err);
  }

  let host = document.getElementById('noRespModal');
  if (!host) {
    host = document.createElement('div');
    host.id = 'noRespModal';
    host.className = 'hidden modal-overlay';
    document.body.appendChild(host);
    modalService.register('noRespModal', host);
  }

  host.innerHTML = /* html */ `
    <div class="modal-container modal-grid modal-auto modal-teal p-6 focus-choice-modal">
      <header class="focus-modal__header mb-4">
        <div class="focus-modal__title-group">
          <h3>No Response</h3>
        </div>
      </header>
      <form id="nr-form" class="reason-list focus-choice-modal__panel">
        ${noRespReasons
          .map((reason) => {
            const label = reason.label
              .replace(/&/g, '&amp;')
              .replace(/</g, '&lt;')
              .replace(/>/g, '&gt;')
              .replace(/"/g, '&quot;')
              .replace(/'/g, '&#39;');
            const value = reason.value
              .replace(/&/g, '&amp;')
              .replace(/</g, '&lt;')
              .replace(/>/g, '&gt;')
              .replace(/"/g, '&quot;')
              .replace(/'/g, '&#39;');
            const tooltip = reason.tooltip
              .replace(/&/g, '&amp;')
              .replace(/</g, '&lt;')
              .replace(/>/g, '&gt;')
              .replace(/"/g, '&quot;')
              .replace(/'/g, '&#39;');
            const tooltipAttr = tooltip
              ? ` data-focus-tooltip="${tooltip}"`
              : '';
            return `
        <label class="reason-btn ui-select-surface"${tooltipAttr}>
          <input type="radio" name="nr" value="${value}" />
          <span>${label}</span>
        </label>`;
          })
          .join('')}
        <div id="oofDate" class="timing-date hidden">
          <label class="d-flex align-items-center gap-2">
            <span>Next Touch</span>
            <input type="date" name="nextTouch" class="form-control" />
          </label>
        </div>
      </form>
      <footer class="focus-modal__footer focus-choice-modal__footer">
        <div
          class="focus-modal__footer-actions"
          data-actions="1"
        >
          <button id="nr-cancel" type="button" class="btn-pill btn--grey btn--info-sm btn--secondary-action" data-focus-tooltip="Close without saving a no-response outcome.">Cancel</button>
        </div>
        <div
          class="focus-modal__footer-actions"
          data-actions="${hasAnother ? '2' : '1'}"
        >
          ${hasAnother ? '<button id="nr-call" type="button" class="btn-pill btn--teal btn--info-sm btn--primary-action focus-modal__alternate-action" data-focus-tooltip="Save this no-response reason and call the next available number." disabled>Call Another Line</button>' : ''}
          <button id="nr-next" type="button" class="btn-pill btn--orange btn--info-sm btn--primary-action" data-focus-tooltip="Save this no-response outcome and move on." disabled>Save</button>
        </div>
      </footer>
    </div>`;

  applyFocusModeTooltips(host, { enableTooltips });

  return new Promise((resolve) => {
    const form = host.querySelector('#nr-form');
    const btnCall = host.querySelector('#nr-call');
    const btnNext = host.querySelector('#nr-next');
    const btnCancel = host.querySelector('#nr-cancel');
    const dateRow = host.querySelector('#oofDate');
    const dateInput = host.querySelector('input[name="nextTouch"]');

    const enable = () => {
      const checked = form.querySelector('input[name="nr"]:checked');
      const reason = noRespReasons.find(
        (item) => item.value === checked?.value
      );
      const needsDate = !!reason?.allowsNextTouch;
      const hasDate = !needsDate || !!dateInput.value;
      const dis = !checked || !hasDate;
      if (btnCall) btnCall.disabled = dis || needsDate;
      if (btnNext) btnNext.disabled = dis;
    };

    const toggleDate = () => {
      const sel =
        form.querySelector('input[type="radio"][name="nr"]:checked')?.value ||
        '';
      const reason = noRespReasons.find((item) => item.value === sel);
      dateRow.classList.toggle('hidden', !reason?.allowsNextTouch);
      enable();
    };

    form.addEventListener('change', toggleDate);
    dateInput.addEventListener('input', enable);
    toggleDate();

    const close = (val) => {
      modalService.close('noRespModal');
      form.removeEventListener('change', toggleDate);
      dateInput.removeEventListener('input', enable);
      btnCall?.removeEventListener('click', onCall);
      btnNext.removeEventListener('click', onNext);
      resolve(val);
    };

    const onCall = () => {
      const val = form.nr.value;
      if (!val) return;
      const reason = noRespReasons.find((item) => item.value === val);
      close({
        option: val,
        action: 'call',
        nextTouch: dateInput.value,
        deleteContactInfo: reason?.deleteContactInfo === true,
        metadata: { noRespReason: val },
      });
    };
    const onCancel = () => close(null);

    const onNext = () => {
      const val = form.nr.value;
      if (!val) return;
      const reason = noRespReasons.find((item) => item.value === val);
      close({
        option: val,
        action: 'next',
        nextTouch: dateInput.value,
        deleteContactInfo: reason?.deleteContactInfo === true,
        metadata: { noRespReason: val },
      });
    };

    if (btnCall) btnCall.addEventListener('click', onCall);
    btnCancel.addEventListener('click', onCancel);
    btnNext.addEventListener('click', onNext);

    modalService.open('noRespModal');
  });
}
